import React from "react";
import { Button, message } from "antd";
import styled from "styled-components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";

const TikTokAuthContainer = styled.div`
	text-align: center;
	padding: 2rem 0;
`;

const ProfileSection = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: center;
	gap: 1rem;
	margin-bottom: 2rem;
`;

const ProfileCard = styled.div`
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	min-height: 100px;
`;

const AvatarContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 0.5rem;
`;

const TextContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const ProfileAvatar = styled.div`
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background: linear-gradient(135deg, #ff0050, #00f2ea);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-weight: bold;
	font-size: 1.2rem;
	margin-bottom: 0.5rem;
`;

const ProfileName = styled.div`
	font-size: 0.875rem;
	font-weight: 500;
	color: #333;
	margin-bottom: 0.25rem;
`;

const SwitchAccount = styled.div`
	font-size: 0.75rem;
	color: #666;
	text-decoration: underline;
	cursor: pointer;
`;

const ArrowIcon = styled.div`
	font-size: 1.5rem;
	color: #999;
`;

const PermissionsSection = styled.div`
	text-align: left;
	margin-bottom: 2rem;
`;

const PermissionsTitle = styled.h3`
	font-size: 1rem;
	font-weight: 600;
	color: #333;
	margin: 0 0 1rem 0;
`;

const PermissionItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.75rem 0;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		border-bottom: none;
	}
`;

const PermissionText = styled.span`
	font-size: 0.875rem;
	color: #333;
	flex: 1;
	margin-right: 1rem;
`;

const ToggleSwitch = styled.div<{ active: boolean }>`
	width: 44px;
	height: 24px;
	background: ${(props) => (props.active ? CUSTOM_COLORS.PRIMARY : "#d9d9d9")};
	border-radius: 12px;
	position: relative;
	cursor: pointer;
	transition: background-color 0.2s;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		right: ${(props) => (props.active ? "2px" : "22px")};
		width: 20px;
		height: 20px;
		background: white;
		border-radius: 50%;
		transition: right 0.2s;
	}
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
`;

const CancelButton = styled(Button)`
	height: 40px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	border: 1px solid #d9d9d9;
	background: white;
	color: #333;
	width: 120px;

	&:hover {
		border-color: ${CUSTOM_COLORS.PRIMARY};
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const ContinueAuthButton = styled(Button)`
	height: 40px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PINK};
	border: none;
	color: white;
	width: 120px;

	&:hover {
		background: ${CUSTOM_COLORS.PINK} !important;
		opacity: 0.9;
	}
`;

interface TikTokAuthStepProps {
	permissions: {
		profileInfo: boolean;
		userAnalytics: boolean;
		publicVideos: boolean;
		videoAnalytics: boolean;
		username: boolean;
		engagementStats: boolean;
		accountType: boolean;
	};
	onPermissionToggle: (permission: keyof TikTokAuthStepProps["permissions"]) => void;
	loading: boolean;
}

const TikTokAuthStep: React.FC<TikTokAuthStepProps> = ({ permissions, onPermissionToggle, loading }) => {
	const handleCancel = () => {
		message.info("Cancelled TikTok connection");
	};

	return (
		<TikTokAuthContainer>
			<ProfileSection>
				<ProfileCard>
					<AvatarContainer>
						<ProfileAvatar>N</ProfileAvatar>
					</AvatarContainer>
					<TextContainer>
						<ProfileName>nam_amplify</ProfileName>
						<SwitchAccount>Switch account</SwitchAccount>
					</TextContainer>
				</ProfileCard>

				<ArrowIcon>↔</ArrowIcon>

				<ProfileCard>
					<AvatarContainer>
						<ProfileAvatar style={{ background: "linear-gradient(135deg, #3A0CA3, #4460EF)" }}>
							C
						</ProfileAvatar>
					</AvatarContainer>
					<TextContainer>
						<ProfileName>Creatorverse</ProfileName>
					</TextContainer>
				</ProfileCard>
			</ProfileSection>

			<PermissionsSection>
				<PermissionsTitle>Creatorverse would like to:</PermissionsTitle>

				<PermissionItem>
					<PermissionText>Access your profile info (avatar and display name)</PermissionText>
					<ToggleSwitch active={permissions.profileInfo} onClick={() => onPermissionToggle("profileInfo")} />
				</PermissionItem>

				<PermissionItem>
					<PermissionText>Read your TikTok user analytics</PermissionText>
					<ToggleSwitch
						active={permissions.userAnalytics}
						onClick={() => onPermissionToggle("userAnalytics")}
					/>
				</PermissionItem>

				<PermissionItem>
					<PermissionText>Read your public videos on TikTok</PermissionText>
					<ToggleSwitch
						active={permissions.publicVideos}
						onClick={() => onPermissionToggle("publicVideos")}
					/>
				</PermissionItem>

				<PermissionItem>
					<PermissionText>Read your TikTok video analytics</PermissionText>
					<ToggleSwitch
						active={permissions.videoAnalytics}
						onClick={() => onPermissionToggle("videoAnalytics")}
					/>
				</PermissionItem>

				<PermissionItem>
					<PermissionText>Read your username</PermissionText>
					<ToggleSwitch active={permissions.username} onClick={() => onPermissionToggle("username")} />
				</PermissionItem>

				<PermissionItem>
					<PermissionText>
						Read your profile engagement statistics, such as like count, follower count, following count,
						and video count
					</PermissionText>
					<ToggleSwitch
						active={permissions.engagementStats}
						onClick={() => onPermissionToggle("engagementStats")}
					/>
				</PermissionItem>

				<PermissionItem>
					<PermissionText>Read your TikTok account type</PermissionText>
					<ToggleSwitch active={permissions.accountType} onClick={() => onPermissionToggle("accountType")} />
				</PermissionItem>
			</PermissionsSection>

			<ButtonGroup>
				<CancelButton onClick={handleCancel}>Cancel</CancelButton>
				<ContinueAuthButton type="primary" htmlType="submit" loading={loading}>
					Continue
				</ContinueAuthButton>
			</ButtonGroup>
		</TikTokAuthContainer>
	);
};

export default TikTokAuthStep;
